import dayjs from 'dayjs';
import { IEntity } from '../../components/core/entity.js';
import { ComponentList } from '../../components/core/component-list.js';
import { MapUtils } from '../../utility/map-utils.js';
import { Currency } from '../serializable/currency.js';
import { Language } from '../serializable/language.js';
import { Price } from '../serializable/price.js';
import { FlexibleServiceDuration } from './flexible-service-duration.js';
import { Component } from '../../components/core/component.js';
import { VersionRange } from '../core/version-range.js';
import { Duration } from 'dayjs/plugin/duration.js';
import { ContactMethod } from '../serializable/contact-method.js';
import { FlexibleServiceDurationComponent } from '../../components/services/flexible-service-duration-component.js';
import { ServiceDurationComponent } from '../../components/services/service-duration-component.js';
import { ServiceModifierComponent } from '../../components/services/service-modifier-component.js';
import { ServicePriceComponent } from '../../components/services/service-price-component.js';
import { ServiceModifier } from '../../components/services/service-modifier.js';
import { PriceRange } from './price-range.js';
import { PriceType } from './price-type.js';
import { LocalizedString } from './localized-string.js';
import { BusinessModuleRef } from './business-module-ref.js';
import { isNotNullOrUndefined, isNullOrUndefined } from '../../utility/null-safety.js';
import { ServiceStaffMembersComponent } from '../../components/services/staff-members-component.js';
import { ServiceCapacityComponent } from '../../components/services/service-capacity-component.js';
import { ServiceAttendanceComponent } from '../../components/services/service-attendance-component.js';
import { ServiceAttendanceGroup } from '../../components/services/service-attendance-group.js';
import { ICopyWithBusinessRef } from '../../contracts/copy-with-business-ref.js';
import {
  DataModel,
  DataModelBuilder,
  fromData,
  isSameData,
  SerializationContext,
  SerializedData,
} from '../dto/dto.js';
import {
  fromEnumAsString,
  fromEnumAsStringOrNull,
  toEnumFromString,
  toEnumFromStringOrNull,
} from '../../serialization/enum-utils.js';

export enum ServiceStatus {
  Live = 'LIVE',
  Archived = 'ARCHIVED'
}

export class Service extends DataModel implements IEntity, ICopyWithBusinessRef {
  static readonly TYPE_ID: string = '0c6514d656674c2e8935143cd5b6fef3';
  static readonly SCHEMA_VERSION: number = 13;

  private constructor(
    public readonly uniqueId: string,
    public readonly businessRef: BusinessModuleRef | null,
    public readonly currency: Currency,
    public readonly isPrivate: boolean,
    public readonly isOnDemand: boolean,
    // HTML hex color in ARGB format(e.g. #FFFF0000 for red)
    public readonly color: string,
    public readonly names: LocalizedString,
    public readonly descriptions: LocalizedString,
    public readonly components: ComponentList,
    public readonly isInviteOnly: boolean,
    public readonly isPriceFixed: boolean,
    public readonly isPricePublic: boolean,
    public readonly status: ServiceStatus,
    public readonly orderIndex: number,
    public readonly contactMethod: ContactMethod | null,
  ) {
    super();
  }

  get $typeId(): string {
    return Service.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(Service.SCHEMA_VERSION);
  }

  get name(): string {
    return this.names.getAnyOrEmpty();
  }

  get description(): string {
    return this.descriptions.getAnyOrEmpty();
  }

  get hasName(): boolean {
    return this.name !== null && this.name.length > 0;
  }

  get isPublic(): boolean {
    return !this.isPrivate;
  }

  get isCallOnly(): boolean {
    return this.contactMethod === ContactMethod.Call;
  }

  get isMessageOnly(): boolean {
    return this.contactMethod === ContactMethod.Message;
  }

  static fromMap(context: SerializationContext, map: SerializedData): Service {
    return new Service(
        MapUtils.get<string>(map, 'uniqueId'),
        BusinessModuleRef.decode(MapUtils.getOrNull<string>(map, 'businessRef')),
        toEnumFromString(MapUtils.get<string>(map, 'currency'), Currency),
        MapUtils.get<boolean>(map, 'isPrivate'),
        MapUtils.get<boolean>(map, 'isOnDemand'),
        MapUtils.get<string>(map, 'color'),
        context.fromData<LocalizedString>(MapUtils.getRaw(map, 'names') as SerializedData),
        context.fromData<LocalizedString>(MapUtils.getRaw(map, 'descriptions') as SerializedData),
        ComponentList.deserialize(context, MapUtils.get<SerializedData[]>(map, 'components')),
        MapUtils.get<boolean>(map, 'isInviteOnly'),
        MapUtils.get<boolean>(map, 'isPriceFixed'),
        MapUtils.get<boolean>(map, 'isPricePublic'),
        toEnumFromString(MapUtils.get<string>(map, '$status'), ServiceStatus),
        MapUtils.get<number>(map, 'orderIndex'),
        toEnumFromStringOrNull(MapUtils.getOrNull<string>(map, 'contactMethod'), ContactMethod),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'businessRef': this.businessRef?.encode() ?? null,
      'names': context.toData(this.names),
      'descriptions': context.toData(this.descriptions),
      'isPrivate': this.isPrivate,
      'isOnDemand': this.isOnDemand,
      'isPricePublic': this.isPricePublic,
      'currency': fromEnumAsString(this.currency),
      'color': this.color,
      'components': this.components.serialize(context),
      'isPriceFixed': this.isPriceFixed,
      'isInviteOnly': this.isInviteOnly,
      '$status': fromEnumAsString(this.status),
      'orderIndex': this.orderIndex,
      'contactMethod': fromEnumAsStringOrNull(this.contactMethod),
    };
  }

  clone(): this {
    return new Service(
        this.uniqueId,
        this.businessRef?.clone() ?? null,
        this.currency,
        this.isPrivate,
        this.isOnDemand,
        this.color,
        this.names.clone(),
        this.descriptions.clone(),
        this.components.clone(),
        this.isInviteOnly,
        this.isPriceFixed,
        this.isPricePublic,
        this.status,
        this.orderIndex,
        this.contactMethod,
    ) as this;
  }

  isSame(other: Service): boolean {
    if(this === other) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        isSameData(other.businessRef, this.businessRef) &&
        isSameData(other.names, this.names) &&
        isSameData(other.descriptions, this.descriptions) &&
        other.components.isSame(this.components) &&
        other.isPrivate === this.isPrivate &&
        other.isOnDemand === this.isOnDemand &&
        other.isPricePublic === this.isPricePublic &&
        other.currency === this.currency &&
        other.color === this.color &&
        other.isPriceFixed === this.isPriceFixed &&
        other.isInviteOnly === this.isInviteOnly &&
        other.status === this.status &&
        other.orderIndex === this.orderIndex &&
        other.contactMethod === this.contactMethod;
  }

  copyWithBusinessRef(businessRef: BusinessModuleRef): this {
    return new Service(
        this.uniqueId,
        businessRef.clone(),
        this.currency,
        this.isPrivate,
        this.isOnDemand,
        this.color,
        this.names.clone(),
        this.descriptions.clone(),
        this.components.copyWithBusinessRef(businessRef),
        this.isInviteOnly,
        this.isPriceFixed,
        this.isPricePublic,
        this.status,
        this.orderIndex,
        this.contactMethod,
    ) as this;
  }

  hasComponentByTypeId(typeId: string): boolean {
    return this.components.hasComponentByTypeId(typeId);
  }

  mustFindComponentByTypeId<C extends Component>(typeId: string): C {
    return this.components.mustFindComponentByTypeId<C>(typeId);
  }

  findComponentByTypeId<C extends Component>(typeId: string): C | null {
    return this.components.findComponentByTypeId<C>(typeId);
  }

  findComponentByInstanceId<C extends Component>(instanceId: string): C | null {
    return this.components.findComponentByInstanceId<C>(instanceId);
  }

  findComponentsByTypeId<C extends Component>(typeId: string): C[] {
    return this.components.findComponentsByTypeId<C>(typeId);
  }

  addComponent(component: Component): void {
    this.components.addComponent(component);
  }

  addComponents(list: Component[]): void {
    this.components.addComponents(list);
  }

  removeComponentByTypeId(typeId: string): boolean {
    return this.components.removeComponentByTypeId(typeId);
  }

  removeComponentByInstanceId(instanceId: string): boolean {
    return this.components.removeComponentByInstanceId(instanceId);
  }

  removeComponentAt(index: number): void {
    this.components.removeComponentAt(index);
  }

  removeAllComponents(): void {
    this.components.removeAllComponents();
  }

  popAllComponentsByTypeId<C extends Component>(typeId: string): C[] {
    return this.components.popAllComponentsByTypeId(typeId);
  }

  popComponentByTypeId<C extends Component>(typeId: string): C | null {
    return this.components.popComponentByTypeId(typeId);
  }

  popComponent<C extends Component>(component: C): C | null {
    return this.components.popComponent(component);
  }

  replaceComponent<C extends Component>(replacement: C): boolean {
    return this.components.replaceComponent(replacement);
  }

  addOrReplaceComponent<C extends Component>(component: C): void {
    this.components.addOrReplaceComponent(component);
  }

  mustFindComponentField<C extends Component, F>(componentTypeId: string, find: (c: C) => F): F {
    return this.components.mustFindComponentField<C, F>(componentTypeId, find);
  }

  findComponentField<C extends Component, F>(componentTypeId: string, find: (c: C) => F): F | null {
    return this.components.findComponentField<C, F>(componentTypeId, find);
  }

  //    EXTENSION METHODS
  isBasic(): boolean {
    return this.hasComponentByTypeId(ServicePriceComponent.TYPE_ID);
  }

  isComposite(): boolean {
    return this.hasComponentByTypeId(ServiceModifierComponent.TYPE_ID);
  }

  getDuration(): Duration {
    const component = this.findComponentByTypeId(ServiceDurationComponent.TYPE_ID);
    if(component && component instanceof ServiceDurationComponent) {
      return component.durationRange.to;
    }

    const components = this.findComponentsByTypeId(ServiceModifierComponent.TYPE_ID)
        .filter((c) => c instanceof ServiceModifierComponent)
        .map((c) => c as ServiceModifierComponent);

    return getDurationFromServiceModifiers(components);
  }

  getMinimumDuration(): Duration {
    const component = this.findComponentByTypeId(ServiceDurationComponent.TYPE_ID);
    if(component && component instanceof ServiceDurationComponent) {
      return component.durationRange.from;
    }

    const components = this.findComponentsByTypeId(ServiceModifierComponent.TYPE_ID)
        .filter((c) => c instanceof ServiceModifierComponent)
        .map((c) => c as ServiceModifierComponent);

    return getMinimumDurationFromServiceModifiers(this.currency, components);
  }

  getPrice(): PriceRange {
    const priceComponent =
      this.components.findComponentByTypeId<ServicePriceComponent>(ServicePriceComponent.TYPE_ID);
    if(isNotNullOrUndefined(priceComponent)) {
      return getPriceFromPriceComponent(priceComponent, this.currency);
    }

    const attendanceComponent =
      this.components.findComponentByTypeId<ServiceAttendanceComponent>(ServiceAttendanceComponent.TYPE_ID);
    if(isNotNullOrUndefined(attendanceComponent)) {
      return getPriceFromAttendanceComponent(attendanceComponent, this.currency);
    }

    const modifierComponents =
      this.components.findComponentsByTypeId<ServiceModifierComponent>(ServiceModifierComponent.TYPE_ID);
    if(isNotNullOrUndefined(modifierComponents) && modifierComponents.length >= 0) {
      return getPriceFromServiceModifiers(modifierComponents, this.currency);
    }

    throw new Error('No component found in service to determine price.');
  }

  getPriceType(): PriceType {
    const component = this.findComponentByTypeId(ServicePriceComponent.TYPE_ID);
    if(component && component instanceof ServicePriceComponent) {
      return component.priceType;
    }

    return PriceType.PricePerService;
  }

  getMinimumPrice(): PriceRange {
    const component = this.findComponentByTypeId(ServicePriceComponent.TYPE_ID);
    if(component && component instanceof ServicePriceComponent) {
      return component.priceRanges.get(this.currency) ??
      PriceRange.exactly(Price.withCurrencyFromAmount(this.currency, 0));
    }

    const components = this.findComponentsByTypeId(ServiceModifierComponent.TYPE_ID)
        .filter((c) => c instanceof ServiceModifierComponent)
        .map((c) => c as ServiceModifierComponent);

    return getMinimumPriceRangeFromServiceModifiers(this.currency, components);
  }

  isEveryPricePublic(): boolean {
    const priceComponent =
      this.components.findComponentByTypeId<ServicePriceComponent>(ServicePriceComponent.TYPE_ID);
    if(isNotNullOrUndefined(priceComponent)) {
      return this.isPricePublic;
    }

    const attendanceComponent =
      this.components.findComponentByTypeId<ServiceAttendanceComponent>(ServiceAttendanceComponent.TYPE_ID);
    if(isNotNullOrUndefined(attendanceComponent)) {
      return attendanceComponent.groups.every((group) => group.isPricePublic);
    }

    const modifierComponents =
      this.components.findComponentsByTypeId<ServiceModifierComponent>(ServiceModifierComponent.TYPE_ID);
    if(isNotNullOrUndefined(modifierComponents)) {
      return modifierComponents.every((component) =>
        component.modifiers.every((modifier) =>
          (!modifier.selected) || (modifier.isPricePublic)));
    }

    throw new Error('No component found in service to determine price visibility.');
  }
}

export class ServiceBuilder extends DataModelBuilder<Service> {
  build(context: SerializationContext, map: SerializedData): Service {
    return Service.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(Service.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(Service.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version: number = versionRange.to;

    if(version < 2) {
      MapUtils.set(map, 'isInviteOnly', false);
      version = 2;
    }

    if(version < 3) {
      MapUtils.set(map, '$status', ServiceStatus.Live);
      version = 3;
    }

    if(version < 4) {
      const components = ComponentList.deserialize(
          context, MapUtils.getList<SerializedData>(map, 'components', (data) => data as SerializedData),
      );
      const flexibleDuration = fromData<FlexibleServiceDuration>(
        MapUtils.getRaw(map, 'flexibleDuration') as SerializedData,
      );
      const prices = MapUtils.getMap<Currency, Price>(map, 'prices', (k, v) => {
        return { key: <Currency>k, value: Price.decode(v as string) };
      });

      components.addComponent(FlexibleServiceDurationComponent.create(flexibleDuration));
      components.addComponent(ServicePriceComponent.fromPrices(prices));

      MapUtils.set(map, 'components', components.serialize(context));
      MapUtils.setIfNullOrUndefined(map, 'isPricePublic', true);
      version = 4;
    }

    // Version 5 doesn't introduce any changes. It's just an intermediary version for composite services.
    if(version < 5) {
      version = 5;
    }

    if(version < 6) {
      MapUtils.set(map, 'orderIndex', 0);
      version = 6;
    }

    if(version < 7) {
      MapUtils.set(map, 'contactMethod', ContactMethod.Call);
      version = 7;
    }

    if(version < 8) {
      const components = ComponentList.deserialize(
          context, MapUtils.getList<SerializedData>(map, 'components', (data) => data as SerializedData),
      );
      const component = components.popComponentByTypeId(
          FlexibleServiceDurationComponent.TYPE_ID,
      ) as FlexibleServiceDurationComponent;
      if(component) {
        components.addComponent(ServiceDurationComponent.fromDuration(component.duration));
      }

      MapUtils.set(map, 'components', components.serialize(context));
      version = 8;
    }

    // In this version tags and priority were removed
    if(version < 9) {
      version = 9;
    }

    // In this version:
    //  - `language` was removed
    //  - `localizations` was changed to `names` and type was changed to [LocalizedString]
    //  - `descriptions` type was changed to [LocalizedString]
    if(version < 10) {
      const oldNames = MapUtils.getMap<Language, string>(map, 'localizations', (k, v) => {
        return { key: <Language>k, value: v as string };
      });
      const oldDescriptions = MapUtils.getMap<Language, string>(map, 'descriptions', (k, v) => {
        return { key: <Language>k, value: v as string };
      });

      const names = LocalizedString.localized(oldNames);
      const descriptions = LocalizedString.localized(oldDescriptions);

      MapUtils.set(map, 'names', context.toData(names));
      MapUtils.set(map, 'descriptions', context.toData(descriptions));

      version = 10;
    }

    if(version < 11) {
      const staffMembers = MapUtils.getList<string>(map, 'staffMembers', (value) => value as string);
      const components = ComponentList.deserialize(
          context, MapUtils.getList<SerializedData>(map, 'components', (data) => data as SerializedData),
      );

      components.addComponent(new ServiceStaffMembersComponent(staffMembers));
      MapUtils.set(map, 'components', components.serialize(context));

      version = 11;
    }

    /**
     * In this version the {@link ServiceAttendanceComponent} was introduced for group booking services.
     */
    if(version < 12) {
      const components = ComponentList.deserialize(
          context, MapUtils.getList<SerializedData>(map, 'components', (data) => data as SerializedData),
      );
      const capacityComponent = components.findComponentByTypeId<ServiceCapacityComponent>(
          ServiceCapacityComponent.TYPE_ID
      );

      if(capacityComponent !== null) {
        const priceComponent = components.findComponentByTypeId(ServicePriceComponent.TYPE_ID) as ServicePriceComponent;
        const attendanceComponent = ServiceAttendanceComponent.empty(priceComponent.priceType);
        if(priceComponent !== null) {
          const placeholderGroup = new ServiceAttendanceGroup(
              LocalizedString.forLanguage(Language.English, 'Group'),
              0,
              priceComponent.priceRanges,
              true,
          );

          attendanceComponent.groups.push(placeholderGroup);
        }

        components.removeComponentByTypeId(ServiceAttendanceComponent.TYPE_ID);
        components.removeComponentByTypeId(ServicePriceComponent.TYPE_ID);
        components.addComponent(attendanceComponent);
      }

      MapUtils.set(map, 'components', components.serialize(context));
      version = 12;
    }

    // In this version the [businessRef] field was introduced. The field will be [null] for older services.
    if(version < 13) {
      version = 13;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}

function getDurationFromServiceModifiers(components: ServiceModifierComponent[]): Duration {
  let millis = 0;
  for(const component of components) {
    for(const modifier of component.modifiers) {
      if(modifier.selected) {
        millis += modifier.maxDuration.asMilliseconds();
      }
    }
  }

  return dayjs.duration(millis, 'milliseconds');
}

function getMinimumDurationFromServiceModifiers(currency: Currency, components: ServiceModifierComponent[]): Duration {
  let result: Duration = dayjs.duration(0);
  for(const component of components) {
    if(component.isOptional) {
      continue;
    }

    let modifier = component.modifiers[0];
    for(let i = 1; i < component.modifiers.length; i++) {
      if(component.modifiers[i].durationRange.to < modifier.durationRange.to) {
        modifier = component.modifiers[i];
      }
    }

    result = result.add(modifier.durationRange.to.asMilliseconds(), 'milliseconds');
  }

  if(result.asMilliseconds() <= 0) {
    const minDuration = findMinimumPositiveDurationFromServiceModifiers(components);
    if(minDuration) {
      result = result.add(minDuration.asMilliseconds(), 'milliseconds');
    }
  }

  return result;
}

function findMinimumPositiveDurationFromServiceModifiers(components: ServiceModifierComponent[]): Duration | null {
  let result: Duration | null = null;
  for(const component of components) {
    for(const modifier of component.modifiers) {
      const duration = modifier.durationRange.to;
      if(duration.asMilliseconds() > 0) {
        if(duration < (result ??= duration)) {
          result = duration;
        }
      }
    }
  }

  return result;
}

function getPriceFromPriceComponent(component: ServicePriceComponent, currency: Currency): PriceRange {
  const result = component.priceRanges.get(currency);
  if(isNullOrUndefined(result)) {
    throw new Error(`Missing price for currency: ${currency}`);
  }

  return result;
}

function getPriceFromAttendanceComponent(component: ServiceAttendanceComponent, currency: Currency): PriceRange {
  const initialValue = PriceRange.free(currency);
  const result = component.groups
      .map((group: ServiceAttendanceGroup) => {
        const priceRange = group.priceRanges.get(currency);
        if(isNullOrUndefined(priceRange)) {
          throw new Error(`Missing price for currency: ${currency}`);
        }

        return priceRange.scale(group.groupSize);
      })
      .reduceRight((sum: PriceRange, value: PriceRange) => {
        return sum.add(value);
      }, initialValue);

  return result;
}

function getPriceFromServiceModifiers(components: ServiceModifierComponent[], currency: Currency): PriceRange {
  const initialValue = PriceRange.free(currency);
  const result = findSelectedModifiers(components)
      .map((modifier) => {
        const priceRange = modifier.priceRanges.get(currency);
        if(isNullOrUndefined(priceRange)) {
          throw new Error(`Missing price for currency: ${currency}`);
        }

        return priceRange;
      })
      .reduceRight((sum, value) => sum.add(value), initialValue);

  return result;
}

function findSelectedModifiers(components: ServiceModifierComponent[]): ServiceModifier[] {
  const result: ServiceModifier[] = [];
  components.forEach((component: ServiceModifierComponent) =>
    component.modifiers.forEach((modifier: ServiceModifier) => {
      if(modifier.selected) {
        result.push(modifier);
      }
    }));

  return result;
}

function getMinimumPriceRangeFromServiceModifiers(
    currency: Currency, components: ServiceModifierComponent[],
): PriceRange {
  let result: Price = Price.withCurrencyFromAmount(currency, 0);
  for(const component of components) {
    if(component.isOptional) {
      continue;
    }

    let modifier: ServiceModifier = component.modifiers[0];
    for(let i = 1; i < component.modifiers.length; i++) {
      const price = component.modifiers[i].priceRanges.get(currency);
      const modifierPrice = modifier.priceRanges.get(currency);

      if(isNullOrUndefined(price)) {
        continue;
      }

      if(isNullOrUndefined(modifierPrice)) {
        continue;
      }

      if(price.to.value < modifierPrice.to.value) {
        modifier = component.modifiers[i];
      }
    }

    const modifierPrice = modifier.priceRanges.get(currency);
    if(isNotNullOrUndefined(modifierPrice)) {
      result = result.add(modifierPrice.from);
    }
  }

  if(result.value <= 0) {
    const minPrice = findMinimumPositivePriceFromServiceModifiersWithPositiveDuration(currency, components);
    if(minPrice) {
      result = result.add(minPrice.to);
    }
  }

  return PriceRange.exactly(result);
}

function findMinimumPositivePriceFromServiceModifiersWithPositiveDuration(
    currency: Currency, components: ServiceModifierComponent[],
): PriceRange | null {
  let result: Price | null = null;

  for(const component of components) {
    for(const modifier of component.modifiers) {
      const duration = modifier.durationRange.to;
      const priceRange = modifier.priceRanges.get(currency);
      if(isNullOrUndefined(priceRange)) {
        continue;
      }

      const price = priceRange.to;

      if(duration.asMilliseconds() > 0 && price.value > 0) {
        if(price.value < (result ??= price).value) {
          result = price;
        }
      }
    }
  }

  if(result) {
    return PriceRange.exactly(result);
  }

  return null;
}
